import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { AstrologerProfile } from '../../../types/professional';

interface ModuleStats {
  totalAstrologers: number;
  activeAstrologers: number;
  suspendedAstrologers: number;
  totalClients: number;
  totalAnalyses: number;
  completedAnalyses: number;
  errorAnalyses: number;
  totalTokens: number;
  topServices: { service_name: string; count: number }[];
  topAstrologers: { professional_name: string; total_analyses: number; total_clients: number }[];
}

export function AdminAstrologerReportPage() {
  const [stats, setStats]   = useState<ModuleStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      supabase.from('astrologer_profiles').select('*'),
      supabase.from('astrologer_client_analyses').select('service_name, status, tokens_used'),
    ]).then(([{ data: aps }, { data: analyses }]) => {
      const apList = (aps ?? []) as AstrologerProfile[];
      const anList = (analyses ?? []) as { service_name: string; status: string; tokens_used: number }[];

      const serviceCounts: Record<string, number> = {};
      for (const a of anList) serviceCounts[a.service_name] = (serviceCounts[a.service_name] ?? 0) + 1;
      const topServices = Object.entries(serviceCounts)
        .sort(([,a],[,b]) => b - a)
        .slice(0, 10)
        .map(([service_name, count]) => ({ service_name, count }));

      const topAstrologers = [...apList]
        .sort((a, b) => (b.total_analyses ?? 0) - (a.total_analyses ?? 0))
        .slice(0, 10)
        .map(a => ({ professional_name: a.professional_name, total_analyses: a.total_analyses ?? 0, total_clients: a.total_clients ?? 0 }));

      setStats({
        totalAstrologers:   apList.length,
        activeAstrologers:  apList.filter(a => a.is_active && !a.suspended_at).length,
        suspendedAstrologers: apList.filter(a => !!a.suspended_at).length,
        totalClients:       apList.reduce((s, a) => s + (a.total_clients ?? 0), 0),
        totalAnalyses:      anList.length,
        completedAnalyses:  anList.filter(a => a.status === 'completed').length,
        errorAnalyses:      anList.filter(a => a.status === 'error').length,
        totalTokens:        anList.reduce((s, a) => s + (a.tokens_used ?? 0), 0),
        topServices,
        topAstrologers,
      });
      setLoading(false);
    });
  }, []);

  const cardStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '10px', padding: '1.25rem' };

  if (loading) return <div style={{ padding: '2rem', color: 'var(--text-muted)' }}>Carregando relatório…</div>;
  if (!stats) return null;

  return (
    <div>
      <h1 style={{ fontSize: 'clamp(1.1rem,3vw,1.4rem)', color: 'var(--text-primary)', margin: '0 0 1.5rem', fontFamily: 'var(--font-display)' }}>
        Relatório do Módulo
      </h1>

      {/* KPI Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Astrólogos',         value: stats.totalAstrologers },
          { label: 'Ativos',             value: stats.activeAstrologers, color: '#4ade80' },
          { label: 'Suspensos',          value: stats.suspendedAstrologers, color: '#f87171' },
          { label: 'Clientes totais',    value: stats.totalClients },
          { label: 'Análises geradas',   value: stats.totalAnalyses },
          { label: 'Concluídas',         value: stats.completedAnalyses, color: '#4ade80' },
          { label: 'Erros',              value: stats.errorAnalyses, color: stats.errorAnalyses > 0 ? '#f87171' : undefined },
          { label: 'Tokens consumidos',  value: stats.totalTokens.toLocaleString('pt-BR') },
        ].map(m => (
          <div key={m.label} style={cardStyle}>
            <p style={{ margin: '0 0 0.25rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>{m.label}</p>
            <p style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: m.color ?? 'var(--petrol)', fontFamily: 'var(--font-display)' }}>{m.value}</p>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '1rem' }}>
        {/* Top services */}
        <div style={cardStyle}>
          <p style={{ margin: '0 0 0.75rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>Top serviços</p>
          {stats.topServices.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>Sem dados ainda.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              {stats.topServices.map((s, i) => (
                <div key={s.service_name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1 }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: '18px' }}>#{i + 1}</span>
                    <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-primary)', flex: 1 }}>{s.service_name}</span>
                  </div>
                  <span style={{ fontSize: 'var(--text-caption)', fontWeight: 600, color: 'var(--petrol)' }}>{s.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Top astrologers */}
        <div style={cardStyle}>
          <p style={{ margin: '0 0 0.75rem', fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)' }}>Top astrólogos</p>
          {stats.topAstrologers.length === 0 ? (
            <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>Sem dados ainda.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {stats.topAstrologers.map((a, i) => (
                <div key={a.professional_name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: '11px', color: 'var(--text-muted)', minWidth: '18px' }}>#{i + 1}</span>
                    <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.professional_name}</span>
                  </div>
                  <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', flexShrink: 0 }}>{a.total_clients} cli · {a.total_analyses} an</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
